import { useEffect, useMemo, useRef, useState } from "react";
import {
  CheckCircle2,
  Circle,
  Loader2,
  XCircle,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  Search,
  X,
  Maximize2,
} from "lucide-react";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { ErrorTooltip } from "../ui/errorTooltip";
import { cn } from "../../lib/utils";
import { useLiveDocumentProcessingEnabled } from "../../hooks/useLiveDocumentProcessingEnabled";
import {
  PipelineScope,
  useControlSessionPipelineStream,
} from "../../hooks/useControlSessionPipelineStream";
import {
  DocumentPipelineStatus,
  DocumentStepStatus,
  PipelineStepStatus,
} from "../../models/audit/documentPipelineStatus";
import { DerivedDocumentState } from "../../models/audit/documentProcessingStatus";

const STEP_LABELS: Record<string, string> = {
  pre_process: "Pre-process",
  parse: "Parse",
  extract: "Extract",
  match: "Match",
  finalize: "Finalize",
};

const stepLabel = (step: string): string => STEP_LABELS[step] ?? step;

/**
 * Most recent step activity for a document, as epoch millis. Uses the latest
 * `finishedAt`/`startedAt` across the document's steps. A document that has been
 * queued but hasn't started any step yet (no timestamps) is treated as the
 * newest (Infinity), so a just-uploaded document floats to the top.
 */
const latestActivity = (doc: DocumentPipelineStatus): number => {
  const times = doc.steps.flatMap((step) =>
    [step.finishedAt, step.startedAt]
      .filter((t): t is string => t != null)
      .map((t) => Date.parse(t))
      .filter((t) => !Number.isNaN(t)),
  );
  return times.length > 0 ? Math.max(...times) : Number.POSITIVE_INFINITY;
};

const STATE_CHIP: Record<DerivedDocumentState, string> = {
  pending: "bg-stone-100 text-stone-500",
  processing: "bg-blue-50 text-blue-600",
  ready: "bg-green-50 text-green-700",
  failed: "bg-red-50 text-red-600",
  unknown: "bg-stone-100 text-stone-500",
};

const STATE_LABEL: Record<DerivedDocumentState, string> = {
  pending: "Pending",
  processing: "Processing",
  ready: "Ready",
  failed: "Failed",
  unknown: "Unknown",
};

const StepIcon = ({ status }: { status: PipelineStepStatus }) => {
  switch (status) {
    case "completed":
      return <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" />;
    case "running":
    case "queued":
      return (
        <Loader2 className="h-3.5 w-3.5 text-blue-600 shrink-0 animate-spin" />
      );
    case "failed":
    case "cancelled":
      return <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />;
    default:
      return <Circle className="h-3.5 w-3.5 text-stone-300 shrink-0" />;
  }
};

const StepPill = ({ step }: { step: DocumentStepStatus }) => {
  const isFailed = step.status === "failed" || step.status === "cancelled";
  const isActive = step.status === "running" || step.status === "queued";
  const textColor = isFailed
    ? "text-red-600"
    : isActive
      ? "text-blue-600 font-medium"
      : step.status === "completed"
        ? "text-stone-600"
        : "text-stone-400";

  const body = (
    <span className="flex items-center gap-1 whitespace-nowrap">
      <StepIcon status={step.status} />
      <span className={cn("text-[11px]", textColor)}>
        {stepLabel(step.step)}
      </span>
    </span>
  );

  if (isFailed && step.errorMessage) {
    return <ErrorTooltip message={step.errorMessage}>{body}</ErrorTooltip>;
  }

  return body;
};

const DocumentRow = ({ doc }: { doc: DocumentPipelineStatus }) => (
  <div className="flex flex-col gap-1.5 px-3 py-2.5">
    <div className="flex items-center gap-2">
      <span
        className="truncate text-[13px] text-stone-800"
        title={doc.fileName ?? undefined}
      >
        {doc.fileName ?? doc.documentId}
      </span>
      <span
        className={cn(
          "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide",
          STATE_CHIP[doc.derivedState],
        )}
      >
        {STATE_LABEL[doc.derivedState]}
      </span>
    </div>
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
      {doc.steps.map((step, index) => (
        <span key={step.step} className="flex items-center gap-1.5">
          <StepPill step={step} />
          {index < doc.steps.length - 1 && (
            <ChevronRight className="h-3 w-3 text-stone-300" />
          )}
        </span>
      ))}
    </div>
  </div>
);

const SummaryIndicator = ({
  state,
}: {
  state: DerivedDocumentState | null;
}) => {
  switch (state) {
    case "failed":
      return <AlertTriangle className="h-4 w-4 shrink-0 text-red-500" />;
    case "processing":
      // Pulsing amber "live" dot while documents are still processing.
      return (
        <span className="relative flex h-2.5 w-2.5 shrink-0 items-center justify-center">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-500" />
        </span>
      );
    case "ready":
      return <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />;
    default:
      return <Circle className="h-4 w-4 shrink-0 text-stone-400" />;
  }
};

interface DocumentProcessingToastProps {
  /** The stream subject: a control session id, or (scope="control") a control id. */
  subjectId: string | null;
  scope?: PipelineScope;
  /** Bump (e.g. an upload nonce) to re-surface the toast after new uploads. */
  reconnectKey?: number;
  /** Called when the pipeline transitions into a terminal state (ready/failed) —
   * e.g. to refetch a document list so settled states are reflected. */
  onTerminal?: () => void;
}

/**
 * Google-Docs-style floating status toast for the document-processing
 * pipeline. Slides in at the bottom-right while documents are actively
 * processing (or when a document fails), showing a live per-document /
 * per-step view (pre-process → parse → extract → match → finalize) with
 * inline failure messages. Collapses to a single summary line and re-appears on
 * the next upload. Once surfaced it stays until the user dismisses it (or the
 * page is refreshed) rather than auto-hiding on completion, so a stable summary
 * of what just finished remains. A maximized modal likewise stays open until the
 * user closes it — even after completion — so it never snaps shut mid-view.
 *
 * Gated to Midship users. Owns a single SSE stream — render once per page.
 */
export const DocumentProcessingToast = ({
  subjectId,
  scope = "control-session",
  reconnectKey = 0,
  onTerminal,
}: DocumentProcessingToastProps) => {
  const liveViewEnabled = useLiveDocumentProcessingEnabled();

  const { documents, overallState, error } = useControlSessionPipelineStream(
    liveViewEnabled ? subjectId : null,
    reconnectKey,
    scope,
  );

  // Fire onTerminal on the transition INTO a terminal state (not on every render
  // while terminal), so consumers can refetch their document lists exactly once.
  const prevOverallStateRef = useRef<typeof overallState>(null);
  useEffect(() => {
    const previous = prevOverallStateRef.current;
    prevOverallStateRef.current = overallState;
    if (
      overallState !== previous &&
      (overallState === "ready" || overallState === "failed")
    ) {
      onTerminal?.();
    }
  }, [overallState, onTerminal]);
  const [expanded, setExpanded] = useState(true);
  // Dismissal is a user decision that outlives remounts and revisits: persisted
  // per subject for the browser session, cleared only by a new upload here.
  // Without this, leaving and re-entering the page while documents are still
  // processing resurrects a toast the user already closed.
  const dismissalKey = subjectId
    ? `docProcessingToastDismissed:${scope}:${subjectId}`
    : null;
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (dismissalKey) {
      setDismissed(sessionStorage.getItem(dismissalKey) === "1");
    }
  }, [dismissalKey]);
  const dismiss = () => {
    setDismissed(true);
    if (dismissalKey) sessionStorage.setItem(dismissalKey, "1");
  };
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [modalSearch, setModalSearch] = useState("");
  // Latches true once live activity is observed; persists the toast through
  // completion and resets only on a full remount (page refresh).
  const surfacedRef = useRef(false);

  // Distinguishes this visit's uploads from the mount-time nonce, so a historic
  // failure alone can't surface the toast but one following an upload made here can.
  const initialReconnectKeyRef = useRef(reconnectKey);
  const uploadedThisVisit = reconnectKey !== initialReconnectKeyRef.current;

  // A new upload (nonce bump) re-surfaces the toast even if previously
  // dismissed — including a dismissal persisted from an earlier visit. The
  // mount-time run must not clear a persisted dismissal, hence the guard.
  useEffect(() => {
    if (reconnectKey === initialReconnectKeyRef.current) return;
    setDismissed(false);
    if (dismissalKey) sessionStorage.removeItem(dismissalKey);
    setExpanded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconnectKey]);

  const counts = useMemo(() => {
    const c = { processing: 0, ready: 0, failed: 0 };
    for (const doc of documents) {
      if (doc.derivedState === "processing") c.processing += 1;
      else if (doc.derivedState === "ready") c.ready += 1;
      else if (doc.derivedState === "failed") c.failed += 1;
    }
    return c;
  }, [documents]);

  // Most recent first, so freshly uploaded/processing documents lead the list.
  const sortedDocuments = useMemo(
    () => [...documents].sort((a, b) => latestActivity(b) - latestActivity(a)),
    [documents],
  );

  const filterDocs = (query: string) => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedDocuments;
    return sortedDocuments.filter((doc) =>
      (doc.fileName ?? doc.documentId).toLowerCase().includes(q),
    );
  };

  const filtered = useMemo(
    () => filterDocs(search),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortedDocuments, search],
  );

  const modalFiltered = useMemo(
    () => filterDocs(modalSearch),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortedDocuments, modalSearch],
  );

  const active = overallState === "processing";
  const hasFailure = counts.failed > 0 || overallState === "failed";

  // The toast surfaces only for THIS visit's activity — live processing, or a
  // failure after an upload made here — and then *stays* until the user
  // dismisses it (or the page is refreshed, which resets this ref). It does not
  // auto-hide on completion, so the user keeps a stable summary of what just
  // finished. A page opened onto only historic terminal states (ready OR
  // failed) never surfaces it: failed is finished too, and settled failures
  // are already visible where the documents live. Latching in a ref during
  // render (rather than via an effect) means the toast appears on the very
  // render activity is first observed.
  if (active || (hasFailure && uploadedThisVisit)) surfacedRef.current = true;

  const toastVisible =
    liveViewEnabled &&
    !dismissed &&
    documents.length > 0 &&
    surfacedRef.current;

  // The modal, once opened by the user, stays mounted until they close it —
  // even after every document finishes — so it never jarringly snaps shut
  // mid-view. It never auto-opens (modalOpen starts false), so a refresh won't
  // resurrect it. Render nothing when the feature is off or there's nothing to
  // show and the modal is closed.
  if (!liveViewEnabled || (!toastVisible && !modalOpen)) return null;

  // Present as a failure once active work has settled and a failure remains;
  // while still processing, keep the "processing" framing (the failed count in
  // the summary still surfaces the problem) so the pulse stays meaningful. Once
  // everything finishes cleanly (only reachable with the modal still open) the
  // view reads as complete.
  const showAsFailed = hasFailure && !active;
  const headerState: DerivedDocumentState | null = showAsFailed
    ? "failed"
    : overallState;
  const title = showAsFailed
    ? "Processing issues"
    : active
      ? "Processing documents"
      : "Processing complete";

  const summaryParts = [
    counts.processing > 0 && `${counts.processing} processing`,
    counts.failed > 0 && `${counts.failed} failed`,
    counts.ready > 0 && `${counts.ready} ready`,
  ].filter(Boolean) as string[];

  return (
    <>
      {toastVisible && (
        <div className="fixed bottom-4 right-4 z-50 w-96 border bg-white shadow-lg animate-in slide-in-from-bottom-4 fade-in duration-200">
          <div className="flex items-center justify-between gap-2 border-b border-stone-100 px-3 py-2">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="flex min-w-0 items-center gap-2 text-left"
            >
              <SummaryIndicator state={headerState} />
              <span className="truncate text-sm font-medium text-stone-800">
                {title}
              </span>
              <span className="truncate text-xs text-stone-400">
                {summaryParts.join(" · ")}
              </span>
            </button>
            <div className="flex shrink-0 items-center gap-0.5">
              <button
                type="button"
                onClick={() => {
                  setModalSearch(search);
                  setModalOpen(true);
                }}
                aria-label="Open full view"
                className="rounded p-1 text-stone-400 hover:bg-stone-100"
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                aria-label={expanded ? "Collapse" : "Expand"}
                className="rounded p-1 text-stone-400 hover:bg-stone-100"
              >
                <ChevronDown
                  className={cn(
                    "h-4 w-4 transition-transform duration-200",
                    expanded && "rotate-180",
                  )}
                />
              </button>
              <button
                type="button"
                onClick={dismiss}
                aria-label="Dismiss"
                className="rounded p-1 text-stone-400 hover:bg-stone-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {expanded && (
            <>
              {documents.length > 5 && (
                <div className="relative px-3 py-2">
                  <Search className="pointer-events-none absolute left-5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-stone-400" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search documents"
                    className="h-8 pl-7 text-sm"
                  />
                </div>
              )}
              <div className="max-h-80 divide-y divide-stone-100 overflow-y-auto">
                {filtered.length === 0 ? (
                  <div className="px-3 py-6 text-center text-xs text-stone-400">
                    {error
                      ? "Live status temporarily unavailable."
                      : `No documents match “${search}”.`}
                  </div>
                ) : (
                  filtered.map((doc) => (
                    <DocumentRow key={doc.documentId} doc={doc} />
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="flex max-h-[80vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
          <DialogHeader className="border-b border-stone-100 px-5 py-4">
            <DialogTitle className="flex items-center gap-2 text-base">
              <SummaryIndicator state={headerState} />
              {title}
              <span className="text-sm font-normal text-stone-400">
                {summaryParts.join(" · ")}
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="relative px-5 py-3">
            <Search className="pointer-events-none absolute left-7 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
            <Input
              value={modalSearch}
              onChange={(e) => setModalSearch(e.target.value)}
              placeholder="Search documents"
              className="h-9 pl-8"
            />
          </div>

          <div className="flex-1 divide-y divide-stone-100 overflow-y-auto border-t border-stone-100">
            {modalFiltered.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-stone-400">
                {error
                  ? "Live status temporarily unavailable."
                  : `No documents match “${modalSearch}”.`}
              </div>
            ) : (
              modalFiltered.map((doc) => (
                <DocumentRow key={doc.documentId} doc={doc} />
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
